import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * GuidedTour — Reusable spotlight walkthrough matching the exact v2 portal walkthrough flow and design.
 */

const TOUR_STYLES = `
/* Full-screen overlay container */
.v2-tour-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.v2-tour-overlay.is-active {
  opacity: 1;
  pointer-events: auto;
}

/* Ring Spotlight Cutout */
.v2-tour-ring {
  position: fixed;
  border-radius: 14px;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 4px #12A196, 0 0 0 9999px rgba(9, 40, 37, 0.70);
  pointer-events: auto;
  cursor: pointer;
  z-index: 100000;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

/* Coach Card */
.v2-tour-coach {
  position: fixed;
  z-index: 100005;
  width: 376px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 28px 70px -22px rgba(9, 40, 37, 0.55);
  padding: 22px 22px 18px;
  pointer-events: auto;
  transition: transform 0.3s ease, left 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

/* Coach Pointer Arrow */
.v2-tour-pointer {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #ffffff;
  border-radius: 2px;
  z-index: 1;
  pointer-events: none;
}

/* Kick (Step Label) */
.v2-tour-kick {
  font-family: "DM Mono", ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0E7C74;
  font-weight: 600;
}

/* Title */
.v2-tour-title {
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.3px;
  margin: 9px 0 8px;
  line-height: 1.2;
  color: #12312D;
}

/* Body text */
.v2-tour-body {
  font-size: 14px;
  line-height: 1.55;
  color: #5C6C69;
  margin: 0;
}

/* Feature Badge List (Step 2) */
.v2-tour-list {
  list-style: none;
  padding: 0;
  margin: 14px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.v2-tour-list li {
  font-size: 12.5px;
  color: #12312D;
  background: #EAF4F1;
  border: 1px solid #CFE6E0;
  border-radius: 9px;
  padding: 6px 11px;
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 500;
}

.v2-tour-list li::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #12A196;
  flex-shrink: 0;
}

.v2-tour-pro-badge {
  font-size: 9px;
  font-weight: 700;
  color: #0E7C74;
  border: 1px solid #CFE6E0;
  border-radius: 5px;
  padding: 0 5px;
}

/* Footer */
.v2-tour-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 12px;
  border-top: 1px solid #EEF1F0;
  gap: 12px;
}

/* Dots */
.v2-tour-dots {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.v2-tour-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #D7DFDD;
  transition: all 0.3s ease;
}

.v2-tour-dot.is-active {
  width: 22px;
  background: #0E5A52;
}

/* Action Buttons */
.v2-tour-acts {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.v2-tour-btn-skip {
  background: none;
  border: 0;
  color: #8A9794;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 8px;
  font-family: inherit;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.v2-tour-btn-skip:hover {
  color: #12312D;
}

.v2-tour-btn-next {
  background: #0E5A52;
  color: #ffffff;
  border: 0;
  border-radius: 10px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 13.5px;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1;
  transition: background 0.15s ease, transform 0.1s ease;
}

.v2-tour-btn-next:hover {
  background: #0b453f;
}

.v2-tour-btn-next:active {
  transform: scale(0.97);
}

@media (max-width: 640px) {
  .v2-tour-coach {
    width: calc(100vw - 24px);
    padding: 18px 18px 14px;
    border-radius: 14px;
  }
  .v2-tour-title { font-size: 18px; }
  .v2-tour-body { font-size: 13px; }
}

@media (prefers-reduced-motion: reduce) {
  .v2-tour-ring,
  .v2-tour-coach,
  .v2-tour-overlay,
  .v2-tour-dot {
    transition: none !important;
  }
}
`;

const GuidedTour = ({
  isOpen,
  onClose,
  steps = [],
  currentStep: controlledStep,
  onStepChange,
}) => {
  const [internalStep, setInternalStep] = useState(0);
  const currentStep = controlledStep !== undefined ? controlledStep : internalStep;

  const [ringStyle, setRingStyle] = useState({});
  const [coachStyle, setCoachStyle] = useState({});
  const [pointerStyle, setPointerStyle] = useState({});

  const coachRef = useRef(null);
  const trackFrameRef = useRef(null);
  const scrollListenerRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const step = steps[currentStep] || {};

  const changeStep = useCallback((newStep) => {
    setInternalStep(newStep);
    onStepChange?.(newStep);
  }, [onStepChange]);

  // Position ring and coach card around target element
  const positionElements = useCallback(() => {
    if (!isOpen || !steps[currentStep]) return false;

    const s = steps[currentStep];
    const el = document.querySelector(s.target);
    if (!el) return false;

    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;

    const pad = s.side ? 4 : 8;
    setRingStyle({
      top: `${r.top - pad}px`,
      left: `${r.left - pad}px`,
      width: `${r.width + pad * 2}px`,
      height: `${r.height + pad * 2}px`,
    });

    const coachEl = coachRef.current;
    const cw = coachEl ? coachEl.offsetWidth : 352;
    const ch = coachEl ? coachEl.offsetHeight : 210;
    const gap = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top, left, pt;
    const place = s.position || 'bottom';

    if (place === 'bottom') {
      top = r.bottom + pad + gap;
      left = r.left;
      pt = 'top';
    } else if (place === 'top') {
      top = r.top - pad - gap - ch;
      left = r.left;
      pt = 'bottom';
    } else if (place === 'right') {
      top = r.top;
      left = r.right + pad + gap;
      pt = 'left';
    } else {
      top = r.top;
      left = r.left - pad - gap - cw;
      pt = 'right';
    }

    // Auto-flip if overflowing viewport boundaries
    if (place === 'bottom' && top + ch > vh - 10 && r.top - pad - gap - ch > 10) {
      top = r.top - pad - gap - ch;
      pt = 'bottom';
    } else if (place === 'top' && top < 10 && r.bottom + pad + gap + ch < vh - 10) {
      top = r.bottom + pad + gap;
      pt = 'top';
    }

    left = Math.max(16, Math.min(left, vw - cw - 16));
    top = Math.max(16, Math.min(top, vh - ch - 16));

    setCoachStyle({
      top: `${top}px`,
      left: `${left}px`,
    });

    const px = Math.min(Math.max(r.left - left + r.width / 2, 36), cw - 36);
    const py = Math.min(Math.max(r.top - top + Math.min(r.height, 60) / 2, 36), ch - 36);

    const ptr = {};
    if (pt === 'top') {
      ptr.top = '-6px';
      ptr.left = `${px}px`;
      ptr.transform = 'translateX(-50%) rotate(45deg)';
      ptr.boxShadow = '-1px -1px 2px 0 rgba(0, 0, 0, 0.04)';
    } else if (pt === 'bottom') {
      ptr.bottom = '-6px';
      ptr.left = `${px}px`;
      ptr.transform = 'translateX(-50%) rotate(45deg)';
      ptr.boxShadow = '1px 1px 2px 0 rgba(0, 0, 0, 0.04)';
    } else if (pt === 'left') {
      ptr.left = '-6px';
      ptr.top = `${py}px`;
      ptr.transform = 'translateY(-50%) rotate(45deg)';
      ptr.boxShadow = '-1px 1px 2px 0 rgba(0, 0, 0, 0.04)';
    } else if (pt === 'right') {
      ptr.right = '-6px';
      ptr.top = `${py}px`;
      ptr.transform = 'translateY(-50%) rotate(45deg)';
      ptr.boxShadow = '1px -1px 2px 0 rgba(0, 0, 0, 0.04)';
    }
    setPointerStyle(ptr);
    return true;
  }, [isOpen, currentStep, steps]);

  // Frame tracking during smooth scrolling
  const startFrameTracking = useCallback((duration = 550) => {
    const startTime = performance.now();
    const loop = () => {
      positionElements();
      if (performance.now() - startTime < duration) {
        trackFrameRef.current = requestAnimationFrame(loop);
      } else {
        positionElements();
      }
    };
    if (trackFrameRef.current) cancelAnimationFrame(trackFrameRef.current);
    trackFrameRef.current = requestAnimationFrame(loop);
  }, [positionElements]);

  // Scroll target into view and position
  const scrollAndPosition = useCallback((retryCount = 0) => {
    if (!isOpen || !steps[currentStep]) return;

    const s = steps[currentStep];
    const el = document.querySelector(s.target);

    if (!el) {
      if (retryCount < 4) {
        retryTimeoutRef.current = setTimeout(() => {
          scrollAndPosition(retryCount + 1);
        }, 120);
        return;
      }
      if (currentStep < steps.length - 1) {
        changeStep(currentStep + 1);
      } else {
        onClose();
      }
      return;
    }

    positionElements();

    if (!s.side) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      startFrameTracking(550);
    } else {
      startFrameTracking(200);
    }
  }, [isOpen, currentStep, steps, positionElements, startFrameTracking, changeStep, onClose]);

  // Open tour
  useEffect(() => {
    if (isOpen) {
      changeStep(0);
      const timer = setTimeout(() => scrollAndPosition(), 60);
      return () => {
        clearTimeout(timer);
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        if (trackFrameRef.current) cancelAnimationFrame(trackFrameRef.current);
      };
    } else {
      setInternalStep(0);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (trackFrameRef.current) cancelAnimationFrame(trackFrameRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reposition on step change
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => scrollAndPosition(), 60);
      return () => {
        clearTimeout(timer);
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        if (trackFrameRef.current) cancelAnimationFrame(trackFrameRef.current);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Reposition on window resize or scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => {
      if (scrollListenerRef.current) cancelAnimationFrame(scrollListenerRef.current);
      scrollListenerRef.current = requestAnimationFrame(() => {
        positionElements();
      });
    };

    window.addEventListener('resize', handleReposition, { passive: true });
    window.addEventListener('scroll', handleReposition, { capture: true, passive: true });

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, { capture: true });
      if (scrollListenerRef.current) cancelAnimationFrame(scrollListenerRef.current);
    };
  }, [isOpen, positionElements]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      changeStep(currentStep + 1);
    } else {
      // Completed: close and smoothly scroll to create section
      onClose();
      const createEl = document.querySelector('[data-tour="create"]');
      if (createEl) {
        createEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentStep, steps.length, changeStep, onClose]);

  const handleSkip = useCallback(() => {
    onClose();
  }, [onClose]);

  // Clicking directly on the ring advances to next step
  const handleRingClick = (e) => {
    e.stopPropagation();
    handleNext();
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        changeStep(currentStep - 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, handleNext, changeStep, onClose]);

  if (!isOpen || steps.length === 0) return null;

  const isLastStep = currentStep === steps.length - 1;

  return createPortal(
    <>
      <style>{TOUR_STYLES}</style>

      {/* Overlay — Backdrop clicks do not dismiss */}
      <div className={`v2-tour-overlay ${isOpen ? 'is-active' : ''}`}>
        {/* Spotlight Ring Cutout */}
        <div
          className="v2-tour-ring"
          style={ringStyle}
          onClick={handleRingClick}
          title="Click to continue"
        />
      </div>

      {/* Coach Card */}
      <div
        ref={coachRef}
        className="v2-tour-coach"
        style={coachStyle}
      >
        <span className="v2-tour-pointer" style={pointerStyle} />

        <div className="v2-tour-kick">
          {step.kick || `Step ${currentStep + 1} of ${steps.length}`}
        </div>

        <h4 className="v2-tour-title">{step.title}</h4>
        <p className="v2-tour-body">{step.body || step.description}</p>

        {/* Feature List (Step 2) */}
        {step.list && step.list.length > 0 && (
          <ul className="v2-tour-list">
            {step.list.map((item, idx) => {
              const name = typeof item === 'string' ? item : item.name;
              const isPro = typeof item === 'object' && item.pro;
              return (
                <li key={idx}>
                  <span>{name}</span>
                  {isPro && <span className="v2-tour-pro-badge">Pro</span>}
                </li>
              );
            })}
          </ul>
        )}

        <div className="v2-tour-foot">
          {/* Progress Dots */}
          <div className="v2-tour-dots" aria-label={`Step ${currentStep + 1} of ${steps.length}`}>
            {steps.map((_, idx) => (
              <i
                key={idx}
                className={`v2-tour-dot ${idx === currentStep ? 'is-active' : ''}`}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="v2-tour-acts">
            <button
              type="button"
              className="v2-tour-btn-skip"
              onClick={handleSkip}
            >
              Skip
            </button>
            <button
              type="button"
              className="v2-tour-btn-next"
              onClick={handleNext}
            >
              {isLastStep ? (step.nextButtonText || 'Create voucher') : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GuidedTour;
